import { createEffect, createSignal, onCleanup, onMount, Show, useContext } from 'solid-js';
import { Portal } from 'solid-js/web';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { getImageServiceSrcPath } from '../image-component/image-component';
import { CrossIcon } from '../icons-library/cross';
import { ProductImage } from './product-types';
import { NavControls } from '../../ui-components/nav-controls/nav-controls';
import { TabHandler } from '../../tools/tab-handler';
import { lockPageScroll } from '../../tools/scroll-lock';

type ProductImageModalProps = {
    images: ProductImage[];
    initialIndex: number;
    onClose: () => void;
};

export const ProductImageModal = (props: ProductImageModalProps) => {
    const { imagesServiceUrl, supportedImageFormats, localize } = useContext(AppContext);
    const [activeIndex, setActiveIndex] = createSignal(props.initialIndex);
    const [closeButton, setCloseButton] = createSignal<HTMLElement | undefined>();
    const [navContainer, setNavContainer] = createSignal<HTMLDivElement | undefined>();

    const lastTabbableElement = () => {
        const container = navContainer();
        if (!container) return undefined;
        const buttons = container.querySelectorAll<HTMLButtonElement>('button:not([disabled])');
        return buttons[buttons.length - 1];
    };

    const isFirst = () => activeIndex() === 0;
    const isLast = () => activeIndex() === props.images.length - 1;

    const focusFirstEnabledNavButton = () => {
        const nav = navContainer();
        const button = nav?.querySelector<HTMLButtonElement>('button:not([disabled])');
        if (button) {
            button.focus();
        } else {
            closeButton()?.focus();
        }
    };

    const next = () => {
        if (!isLast()) {
            setActiveIndex(activeIndex() + 1);
            if (isLast()) {
                queueMicrotask(focusFirstEnabledNavButton);
            }
        }
    };
    const prev = () => {
        if (!isFirst()) {
            setActiveIndex(activeIndex() - 1);
            if (isFirst()) {
                queueMicrotask(focusFirstEnabledNavButton);
            }
        }
    };

    const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            props.onClose();
            return;
        }
        if (e.key === 'ArrowRight') {
            next();
            return;
        }
        if (e.key === 'ArrowLeft') {
            prev();
        }
    };

    createEffect(() => {
        document.addEventListener('keydown', handleKeydown);
        const releaseScrollLock = lockPageScroll();
        onCleanup(() => {
            document.removeEventListener('keydown', handleKeydown);
            releaseScrollLock();
        });
    });

    onMount(() => {
        closeButton()?.focus();
    });

    const handleOverlayClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.dataset.closeOnClick === 'true') {
            props.onClose();
        }
    };

    const activeImage = () => props.images[activeIndex()];

    return (
        <Portal mount={document.getElementById('root') ?? document.body}>
            <TabHandler refs={{
                firstTabbableElement: closeButton,
                lastTabbableElement,
            }}>
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={localize('product-image-gallery', 'Product image gallery')}
                    class="fixed inset-0 z-[100] flex items-center justify-center bg-cvi-bg-overlay-scrim p-cvi-8"
                    data-close-on-click="true"
                    onClick={handleOverlayClick}
                >
                    <div class="relative bg-cvi-bg-default rounded-cvi-md shadow-elevation-overlay max-w-[1200px] w-full max-h-[800px] p-cvi-6 flex flex-col items-center justify-center">
                        <button
                            type="button"
                            class="icon-button-ghost icon-button-ghost-md absolute top-cvi-6 right-cvi-6 z-10"
                            onClick={() => props.onClose()}
                            ref={(el) => setCloseButton(el)}
                            aria-label={localize('close', 'Close')}
                        >
                            <CrossIcon fill="currentColor" size="medium" />
                        </button>

                        <div class="w-full flex flex-col items-center justify-center px-cvi-4 tablet:px-cvi-12 min-h-0">
                            <div class="relative flex items-end justify-center w-full max-w-[800px] aspect-[4/3] rounded-cvi-lg overflow-hidden">
                                <img
                                    src={getImageServiceSrcPath({
                                        imagesServiceUrl,
                                        supportedImageFormats,
                                        src: activeImage().file.url,
                                        width: 1500,
                                        height: 'AUTO',
                                    })}
                                    alt={activeImage().file?.alt || ''}
                                    {...(activeImage().file?.alt ? {} : { role: 'presentation' })}
                                    class="absolute inset-0 w-full h-full object-contain rounded-cvi-lg pointer-events-none"
                                />
                                <Show when={activeImage().file?.caption}>
                                    <p class="absolute bottom-0 flex-1 body-xs text-cvi-fg-muted text-center px-cvi-8 py-cvi-6">
                                        {activeImage().file.caption}
                                    </p>
                                </Show>
                            </div>
                        </div>

                        <div ref={(el) => setNavContainer(el)}>
                            <NavControls
                                isFirst={isFirst()}
                                isLast={isLast()}
                                onPrev={prev}
                                onNext={next}
                            />
                        </div>
                    </div>
                </div>
            </TabHandler>
        </Portal>
    );
};
